/**
 * Configuracion de metricas disponibles por dataSource del dashboard.
 * Replica portable (en `@heskala/core`) de la misma constante que vive en
 * `convex/system/dashboard/index.ts`. La fuente canonica es la del backend
 * (que la usa para tipar `getDashboardStats`); este archivo existe para que
 * mobile pueda evaluar `dashboardAccess` por metrica sin importar logica de
 * `convex/` (limite arquitectonico del repo).
 *
 * Si anades una metrica, hacelo en ambos lugares o mueve la fuente unica aqui
 * y reexporta desde convex.
 */

import type { DashboardAccessLevel } from "../permissions/dashboard-access";

export interface MetricDefinition {
  key: string;
  label: string;
  types: string[];
  dashboardAccess?: DashboardAccessLevel;
}

export const AVAILABLE_METRICS: Record<string, MetricDefinition[]> = {
  ai: [{ key: "pulse", label: "Pulso del negocio", types: ["ai_pulse"] }],
  quotations: [
    { key: "total", label: "Total cotizaciones", types: ["stat_card"] },
    {
      key: "pendingCount",
      label: "Cotizaciones pendientes",
      types: ["stat_card"],
    },
    {
      key: "byStatus",
      label: "Por estado",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "conversionRate",
      label: "Tasa de conversion",
      types: ["stat_card"],
      dashboardAccess: "admin_only",
    },
    {
      key: "totalValue",
      label: "Valor total",
      types: ["stat_card"],
      dashboardAccess: "admin_only",
    },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
      dashboardAccess: "admin_only",
    },
    {
      key: "recentList",
      label: "Cotizaciones recientes",
      types: ["recent_list"],
    },
  ],
  clients: [
    { key: "total", label: "Total clientes", types: ["stat_card"] },
    {
      key: "activeVsInactive",
      label: "Activos vs Inactivos",
      types: ["pie_chart", "bar_chart"],
    },
    { key: "byType", label: "Por tipo", types: ["pie_chart", "bar_chart"] },
  ],
  products: [
    {
      key: "total",
      label: "Total productos/servicios",
      types: ["stat_card"],
    },
    {
      key: "activeVsInactive",
      label: "Activos vs Inactivos",
      types: ["pie_chart", "bar_chart"],
    },
    { key: "byType", label: "Por tipo", types: ["pie_chart", "bar_chart"] },
    {
      key: "recentList",
      label: "Productos recientes",
      types: ["recent_list"],
    },
  ],
  sales: [
    { key: "total", label: "Total ventas", types: ["stat_card"] },
    { key: "byStatus", label: "Por estado", types: ["bar_chart", "pie_chart"] },
    {
      key: "totalAmount",
      label: "Monto total",
      types: ["stat_card"],
      dashboardAccess: "admin_only",
    },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
      dashboardAccess: "admin_only",
    },
    { key: "recentList", label: "Ventas recientes", types: ["recent_list"] },
  ],
  invoices: [
    { key: "total", label: "Total facturas", types: ["stat_card"] },
    {
      key: "pendingCount",
      label: "Facturas pendientes",
      types: ["stat_card"],
    },
    { key: "byStatus", label: "Por estado", types: ["bar_chart", "pie_chart"] },
    {
      key: "totalAmount",
      label: "Monto total",
      types: ["stat_card"],
      dashboardAccess: "admin_only",
    },
    {
      key: "taxSummary",
      label: "Resumen de impuestos",
      types: ["stat_card"],
      dashboardAccess: "admin_only",
    },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
      dashboardAccess: "admin_only",
    },
    {
      key: "recentList",
      label: "Facturas recientes",
      types: ["recent_list"],
    },
  ],
  work_orders: [
    { key: "total", label: "Total ordenes", types: ["stat_card"] },
    { key: "inProgress", label: "En progreso", types: ["stat_card"] },
    { key: "scheduled", label: "Programadas", types: ["stat_card"] },
    {
      key: "totalCost",
      label: "Costo total",
      types: ["stat_card"],
      dashboardAccess: "admin_only",
    },
    { key: "byStatus", label: "Por estado", types: ["bar_chart", "pie_chart"] },
    {
      key: "byPriority",
      label: "Por prioridad",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
      dashboardAccess: "admin_only",
    },
    {
      key: "recentList",
      label: "Ordenes recientes",
      types: ["recent_list"],
    },
  ],
  inventory: [
    {
      key: "totalProducts",
      label: "Productos con stock",
      types: ["stat_card"],
    },
    {
      key: "totalAvailable",
      label: "Unidades disponibles",
      types: ["stat_card"],
    },
    {
      key: "lowStock",
      label: "Productos stock bajo",
      types: ["stat_card"],
    },
    {
      key: "outOfStock",
      label: "Productos sin stock",
      types: ["stat_card"],
    },
    { key: "withExpired", label: "Con vencidos", types: ["stat_card"] },
    { key: "withDamaged", label: "Con danados", types: ["stat_card"] },
    {
      key: "alertDistribution",
      label: "Distribucion de alertas",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "movementTrend",
      label: "Tendencia de movimientos",
      types: ["line_chart", "area_chart", "bar_chart"],
      dashboardAccess: "admin_only",
    },
    {
      key: "recentMovements",
      label: "Movimientos recientes",
      types: ["recent_list"],
    },
  ],
};
